import axios from 'axios';
import { PatientConsumerRenderProps, withPatientContext } from 'components/patient/PatientContext';
import { SidebarConsumerRenderProps, Views, withSidebarContext } from 'components/sidebar/SidebarContext';
import { TreatmentsConsumerRenderProps, withTreatmentsContext } from 'components/treatments/TreatmentsContext';
import moment from 'moment';
import React, { Fragment } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouterProps, withRouter } from 'react-router';
import { Button, Icon } from 'semantic-ui-react';
import { getAllTreatmentsByPatientId } from 'services/treatmentServices';
import styled from 'styled-components';
import { PatientDto } from 'ts-types/api.types';

const ResultsPane = styled.div`
  flex: 0 0 auto;
  margin-right: 15px;
  min-width: 15rem;
  max-width: 36rem;

  .patient-info-header {
    font-weight: bold;
    font-size: 1.1rem;
    padding-bottom: .5rem;
    border-bottom: 1px solid #e5e5e5;
    margin-bottom: 0.5rem;
  }

  .results-container {
    display: grid;
    grid-template-columns: minmax(min-content, 1fr) minmax(3.5rem, max-content);
    row-gap: 0.35rem;
    column-gap: 1.2rem;
    font-size: 1rem;

    .patient-info-property {
      color: #575757;
    }

    .patient-info-value {
      font-weight: bold;
    }

    .home-cdr-button {
      width: fit-content
    }

    .change-email-icon {
      margin-left: 10px;
      cursor: pointer;
    }
  }

`;

export interface PatientInfoViewProps extends RouterProps,
    MessageSourceProps,
    PatientConsumerRenderProps,
    TreatmentsConsumerRenderProps,
    SidebarConsumerRenderProps {

  patientInfo: PatientDto;
  showButtons: boolean;
  onEditEmailButtonClick: (patientId: number) => void;
}


const PatientInfoView = (props: PatientInfoViewProps) => {

  const {
    patientInfo,
    getMessage,
    history,
    changePatient,
    changeTreatments,
    setActiveTab
  } = props;

  const cancelTokenSource = axios.CancelToken.source();

  const openHomeView = async (patientInfo: PatientDto) => {

    const treatments = await getAllTreatmentsByPatientId(cancelTokenSource, patientInfo.id);

    changePatient(patientInfo);
    changeTreatments(treatments);
    setActiveTab(Views.HOME);
    history.push("/");
  }

  const openClinicalDataRecordView = (patientInfo: PatientDto) => {

    changePatient(patientInfo);
    setActiveTab(Views.CLINICAL_DATA_RECORD);
    history.push("/clinical-data-record");
  }

  if (patientInfo) {
    return (
        <ResultsPane onClick={evt => {
          evt.preventDefault();
          evt.stopPropagation()
        }}>
          <div className="patient-info-header">
            <span>{patientInfo.title && `${patientInfo.title} `}{patientInfo.name} {patientInfo.firstName}</span>
            <span style={{float: "right"}}>[{patientInfo.id}]</span>
          </div>
          <div className="results-container">
            {props.showButtons &&
                <>
                  <Button
                      className="home-cdr-button"
                      size="mini"
                      compact
                      basic
                      color="grey"
                      content={getMessage("header.navigation.home")}
                      onClick={() => openHomeView(patientInfo)}/>
                  <Button
                      className="home-cdr-button"
                      size="mini"
                      compact
                      basic
                      color="grey"
                      content={getMessage("header.navigation.clinicalDataRecord")}
                      onClick={() => openClinicalDataRecordView(patientInfo)}/>
                </>
            }
            <div className="patient-info-property">
              {getMessage("patient.name")}:
            </div>
            <div className="patient-info-value">
              {patientInfo.name} {patientInfo.firstName}
            </div>
            {patientInfo.companyName &&
                <Fragment>
                  <div className="patient-info-property">
                    {getMessage("patient.companyName")}:
                  </div>
                  <div className="patient-info-value">
                    {patientInfo.companyName}
                  </div>
                </Fragment>
            }
            <div className="patient-info-property">
              {getMessage("patient.birthDate")}:
            </div>
            <div className="patient-info-value">
              {moment(patientInfo.birthDate).format("DD.MM.YYYY")}
            </div>
            <div className="patient-info-property">
              {getMessage("address")}:
            </div>
            <div className="patient-info-value">
              {patientInfo.street && `${patientInfo.street}, `}
              {patientInfo.zip && `${patientInfo.zip}, `}
              {patientInfo.city && patientInfo.city}
            </div>

            <Fragment>
              <div className="patient-info-property">
                {getMessage("patient.email")}:
              </div>
              <div className="patient-info-value">
                {patientInfo.email || ""}
                <Icon name={'edit'}
                      color={'grey'}
                      onClick={() => props.onEditEmailButtonClick(patientInfo.id)}
                      className="change-email-icon"
                />
              </div>
            </Fragment>

            {
                patientInfo.telephone &&
                <Fragment>
                  <div className="patient-info-property">
                    {getMessage("patient.telephone")}:
                  </div>
                  <div className="patient-info-value">
                    {patientInfo.telephone}
                  </div>
                </Fragment>
            }

            {
                patientInfo.fax &&
                <Fragment>
                  <div className="patient-info-property">
                    {getMessage("patient.fax")}:
                  </div>
                  <div className="patient-info-value">
                    {patientInfo.fax}
                  </div>
                </Fragment>
            }

            {
                patientInfo.telOffice &&
                <Fragment>
                  <div className="patient-info-property">
                    {getMessage("patient.telOffice")}:
                  </div>
                  <div className="patient-info-value">
                    {patientInfo.telOffice}
                  </div>
                </Fragment>
            }

            {
                patientInfo.mobilePhone &&
                <Fragment>
                  <div className="patient-info-property">
                    {getMessage("patient.mobilePhone")}:
                  </div>
                  <div className="patient-info-value">
                    {patientInfo.mobilePhone}
                  </div>
                </Fragment>
            }

            <div className="patient-info-property">
              {getMessage("mandate")}:
            </div>
            <div className="patient-info-value">
              {patientInfo.mandateName}, {patientInfo.mandateCity}
            </div>
          </div>
        </ResultsPane>
    );
  }

  return <div></div>;
};

export default withRouter(
    withMessages(
        withPatientContext(
            withTreatmentsContext(withSidebarContext(PatientInfoView)))));
